<template>
  <div>
    <bar-chart
    chart-id="social-impact-chart"
    :style="{width: socialImpactChartWidth, height: parentIsCusipSearchResult? 'min(25vh, 150px)' : 'min(20vh, 150px)'}"
    :chart-data="socialImpactChartData"
    :chart-options="socialImpactChartOptions(0, subScoreChartFontSize)"
    />

    <bar-chart v-if="showChart(subScoreNames)"
    chart-id="subscore-chart"
    :style="{width:'min(100%, 450px)', height: subScoreChartHeight}"
    :chart-data="subScoreChartData(true)"
    :chart-options="subScoreChartOptions(0, 'x', subScoreChartFontSize)"
    />

    <div class="d-flex justify-center">
        <v-btn v-if="parentIsCusipSearchResult" class="mt-5" outlined color="info" @click="openDialog()">
            <div>
                <div>Explore</div>
                <div>Social Metrics</div>
            </div>
        </v-btn>
    </div>

    <v-dialog @click:outside="showDialog = false" :value="showDialog" scrollable>
        <v-card>
            <!-- putting mdi-close icon inside v-card-title in a scrollable v-dialog is what makes it sticky -->
            <v-card-title style="height:1vh" class="pa-0">
                <v-spacer></v-spacer>
                <v-btn icon @click="showDialog = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <!-- scrollable v-dialog expects text to be inside v-card-text, otherwise the text overflows the v-card -->
            <v-card-text class="pa-5">
                <title-info title="Social Impact Scores" :url="socialImpactScoresUrl"></title-info>

                <div class="d-flex">
                    <v-col :cols="colsPerSection">
                        <bar-chart
                        :style="{
                            width: '100%',
                            height: 'min(30vh, 200px)',
                            marginRight: '8%'
                        }"
                        :chart-data="socialImpactChartData"
                        :chart-options="socialImpactChartOptions(socialImpactDialogPadding, subScoreChartFontSize)"
                        />
                    </v-col>

                    <v-col :cols="4">
                        <bar-chart
                        :style="{width: '100%', height: 'min(30vh, 200px)'}"
                        chart-id="quartiles"
                        :chart-data="{
                            labels: ['Social Impact Score'],
                            datasets: socialImpactQuartileDatasets
                        }"
                        :chart-options="quartileChartOptions('Quartile Distribution', true)"
                        />
                    </v-col>
                </div>

                <div class="d-flex flex-row">
                    <v-col :cols="colsPerSection">
                        <bar-chart v-if="showChart(subScoreNames)"
                        :style="{
                            width: '100%',
                            height: 'min(50vh, 350px)',
                            marginRight: '5vw'
                        }"
                        :chart-data="subScoreChartData()"
                        :chart-options="subScoreChartOptions(50, 'y', subScoreChartFontSize)"
                        />
                    </v-col>
                    <v-col :cols="4">
                        <bar-chart
                        v-if="showChart(quartileNames) && !this.isCMBS"
                        :style="{width:'100%', height: 'min(50vh, 350px)'}"
                        chart-id="quartiles"
                        :chart-data="{
                            labels: subScoreLabels,
                            datasets: subScoreQuartileDatasets
                        }"
                        :chart-options="quartileChartOptions('Component Score Quartile Distribution', false)"
                        />
                    </v-col>
                </div>

                <v-divider v-if="showUnderlyingData()"/>
                <title-info title="Underlying Data" :url="underlyingDataUrl"></title-info>
                <i v-if="showUnderlyingData()">All percent breakdowns are estimates which add up to approximately, but not always exactly, 100%.</i>


                <v-row>
                    <v-col v-if="showChart(namesLabelsToNames(economicNamesLabelsValues))" :cols="underlyingDataCols">
                        <v-card-title>
                            Economic Measures
                        </v-card-title>

                        <table v-if="showChart(economicNamesLabelsValues.map(nameLabel => nameLabel.name))">
                            <tr v-for="economicNameLabel in economicNamesLabelsValues" v-bind:key="economicNameLabel.name">
                                <td>
                                {{economicNameLabel.label}}
                                </td>
                                <td style="text-align:right;" v-html="economicNameLabel.value"></td>
                            </tr>
                        </table>
                    </v-col>
                    <v-divider vertical v-if="showChart(namesLabelsToNames(economicNamesLabelsValues))" />

                    <v-col :cols="underlyingDataCols" v-if="showChart(workTransPercentNames)">
                        <v-card-title>
                            Transportation
                        </v-card-title>
                        <div class="d-flex flex-row justify-center">
                            <bar-chart v-if="showChart(workTransPercentNames)"
                            :style="{
                                width: '40%',
                                //height: 'min(50vh, 300px)',
                                marginRight: '5vw'
                            }"
                            :chart-data="{
                                labels: ['Commute Methods'],
                                datasets: datasetsFromNamesAndLabels(workTransPercentNamesLabels, true)
                            }"
                            :chart-options="barChartOptions('Commute Methods')"
                            :plugins="[shrinkChartArea]"
                            />
                            <bar-chart
                            v-if="showChart(namesLabelsToNames(commuteTimeNamesLabels))"
                            :style="{width: '30%', height: 'min(50vh, 300px)'}"
                            :chart-data="{
                                labels:['Commute time'],
                                datasets: datasetsFromNamesAndLabels(commuteTimeNamesLabels, true)
                            }"
                            :chart-options="barChartOptions('Commute Time')"
                            :plugins="[shrinkChartArea]"
                            />
                        </div>
                    </v-col>
                </v-row>
                <v-divider v-if="showChart(workTransPercentNames)"></v-divider>

                <v-row>
                        <v-col :cols="underlyingDataCols" v-if="showChart(educationPercentNames)">
                        <v-card-title>
                            Education
                        </v-card-title>
                        <div class="d-flex justify-center">

                        <bar-chart v-if="showChart(educationPercentNames)"
                        :style="{height: 'min(50vh, 350px)', width:'70%'}"
                        :chart-data="{
                            labels:['Education'],
                            datasets: datasetsFromNamesAndLabels(educationPercentNamesLabels, true)
                        }"
                        :chart-options="barChartOptions('Education Breakdown')"
                        :plugins="[shrinkChartArea]"
                        />

                        </div>
                    </v-col>

                    <v-divider vertical v-if="showChart(educationPercentNames)"/>

                    <v-col :cols="underlyingDataCols" v-if="showChart(employmentPercentNames)">
                        <v-card-title >
                            Employment
                        </v-card-title>
                        <div class="d-flex flex-column align-center pb-3">
                            <bar-chart
                            v-if="showChart(employmentPercentNames)"
                            :style="{
                                height:'100px',
                                width:'95%'
                                }"
                            :chart-data="{
                                labels:['Employment'],
                                datasets: datasetsFromNamesAndLabels(employmentPercentNamesLabelsColors, false, true)
                            }"
                            :chart-options="barChartOptions('Employment Breakdown (% of Employed Residents)', false)"
                            :plugins="[shrinkChartArea]"
                            />
                            <table v-if="showChart(employmentPercentNames)" class="pt-5">
                                <tr v-for="employmentGroup in employmentPercentNamesLabelsColors" v-bind:key="employmentGroup.name">
                                    <td :style="`background-color: ${employmentGroup.color}; width: 3vw;`"></td>
                                    <td>{{employmentGroup.label}}</td>
                                    <td>{{`${socialData[employmentGroup.name]}%`}}</td>
                                </tr>
                            </table>
                        </div>
                    </v-col>
                </v-row>
                    <v-divider v-if="showChart(employmentPercentNames)" ></v-divider>
                <v-row>

                    <v-col :cols="underlyingDataCols" v-if="socialData.median_year_housing_built || socialData.pct_kids_under_18">
                        <v-card-title>
                            Population and Households
                        </v-card-title>
                        <table>
                            <tr>
                                <td>
                                    Median Year Housing Built
                                </td>
                                <td style="text-align:right;">
                                    {{socialData.median_year_housing_built}}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Percent Kids Under 18
                                </td>
                                <td style="text-align:right;">
                                    {{`${socialData.pct_kids_under_18}%`}}
                                </td>
                            </tr>
                        </table>
                    </v-col>
                    <v-divider vertical v-if="showChart(employmentPercentNames)" ></v-divider>
                </v-row>
            </v-card-text>
        </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import chroma from 'chroma-js'
import { Line as LineChart, Bar as BarChart } from 'vue-chartjs/legacy'
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
} from "chart.js"
import TitleInfo from '@/components/TitleInfo.vue'

ChartJS.register(
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  BarElement
);

export default {
  data: () => ({
    savedCollection: null,
    subScoreNames: ['low_affluence_score','low_education_score','low_prestige_employment_score','health_obstacle_score','poverty_concentration_score','pct_income_on_housing_score','minority_score'],
    employmentColors: [
        "#987db9",
        "#6aac3f",
        "#8064d8",
        "#cba42b",
        "#ae65c1",
        "#52a667",
        "#d446a8",
        "#45ac95",
        "#d8406d",
        "#4eaad4",
        "#ce4d30",
        "#5c7fcc",
        "#b78b3b",
        "#dc74b5",
        "#8f8a4a",
        "#a2437b",
        "#c5734f",
        "#da84aa",
        "#d06f6f",
        "#a44b62"
    ],
    showDialog: false,
    commuteTimeNamesLabels: [
        {name:'pct_commute_lte20mins', label: '\u2264 20 min'},
        {name: 'pct_commute_20to40mins', label: '20 to 40 min'},{name:'pct_commute_gte40mins', label: '\u2265 40 min'}
    ],
    socialImpactScoresUrl: 'https://docs.level11analytics.com/f_soc_imp.html#the-social-impact-composite-score',
    underlyingDataUrl: 'https://docs.level11analytics.com/f_soc_imp.html#sub-family-commuting-measures'
  }),
  props: {
    socialData: Object,
    isCMBS: Boolean,
    parentIsCusipSearchResult: Boolean
  },
  components: {
    BarChart,
    TitleInfo
  },
  computed: {
    /* things which apply to more than one chart type */
    ...mapState({
      results: state => state.currentCusipData,
      labels: state => state.labels,
      powerpointData: state => state.powerpointData,
      startPowerpointGeneration: state => state.startPowerpointGeneration,
      powerpointDataToGenerate: state => state.powerpointDataToGenerate
    }),
    colsPerSection() {
        switch(this.$vuetify.breakpoint.name) {
            case 'xl': return 4;
            default: return 6
        }
    },
    underlyingDataCols() {
        switch(this.$vuetify.breakpoint.name) {
            case 'xl': return 4;
            default: return 6
        }
    },
    chartDimensions() {
        let dimensions = {}
        let socialImpactHeight = this.parentIsCusipSearchResult? 'min(25vh, 150px)' : 'min(20vh, 150px)'
        dimensions.socialImpact = {width: this.socialImpactChartWidth, }
    },
    chartColor() {
        return this.getNColors(10)[6]
    },
    shrinkChartArea() {
        /* We want the x axis tick marks (marking percentages) to touch the bar chart and the bar chart to be 40px thick. If we just set the bar chart thickness to 40, chart.js will add padding around it, and the tick marks will only come after/before the padding. So instead we want to set bar chart percentage to 100% and the chart height to 40 px. The only way I found of modifying chartArea dimensions was in a plugin like this.
        NB: chartArea is the area of the grid within the axes rather than the canvas element (with legend) as a whole, so we don't have to worry about legend height.
        */
        return {
                    id: 'shrinkChartArea',
                    beforeDraw(chart, args, options) {
                        const {ctx, chartArea: {top}} = chart;
                        ctx.save();
                        let newBottom = top + 40
                        chart.chartArea.bottom = newBottom
                        chart.chartArea.height = 40
                    }
                }
    },

    /* particular charts */
    /*social impact chart */
    socialImpactChartData() {
        return {
            labels: [['Social', 'Impact', 'Score']],
            datasets: [
                {
                    data: [this.socialData.social_impact_score], backgroundColor: this.chartColor
                }
            ]
        }
    },
    socialImpactChartWidth: function() {
        if(this.$vuetify.breakpoint.name === 'xl') {
            return '60%'
        }
        else {
            return '90%'
        }
    },
    socialImpactDialogPadding() {
        switch(this.$vuetify.breakpoint.name) {
            // case 'md': return 200;
            // case 'sm': return 100;
            default: return 100;
        }
    },

    /* subscore chart */
    subScoreChartFontSize() {
         switch(this.$vuetify.breakpoint.name) {
            case 'xl': return 16;
            case 'lg': return 12;
            default: return 10
         }
    },
    subScoreChartHeight() {
        let breakpoint = this.$vuetify.breakpoint.name
        let cusipSize = 'min(40vh, 350px)'
        if(breakpoint === 'xl') {
            return this.parentIsCusipSearchResult? cusipSize : '350px'
        }
        else if(breakpoint == 'lg') {
            return this.parentIsCusipSearchResult? cusipSize: '300px'
        }
        else {
            return this.parentIsCusipSearchResult? cusipSize : '250px'
        }
    },
    subScoreLabels() {
        return this.subScoreNames.map( name => this.stringToName(name).replace('Pct', 'Percent').replace(' Score', ''))
    },
    subScoreLabelsShort() {
       return ['Affluence', 'Education', 'Employment', 'Health', 'Poverty', 'Housing', 'Minority']

    },
    subScoreChartRawData() {
        let data = [];
        this.subScoreNames.forEach(name => {
            data.push(this.socialData[name])
        })
        return data
    },

    /* quartile charts general */
    quartileChartFontSize() {
        switch(this.$vuetify.breakpoint.name) {
            case 'xs': return 8
            case 'sm': return 8
            case 'md': return 10
            case 'lg': return 12
            case 'xl': return 14
        }
    },
    quartileNames() {
        let lowQuartileNames = this.subScoreNames.map(name => {
            return `pct_low_quartile_${name}`
        });
        let topQuartileNames = this.subScoreNames.map(name => {
            return `pct_top_quartile_${name}`
        });

        return lowQuartileNames.concat(topQuartileNames)
    },
    /* social impact quartile chart */
    socialImpactQuartileDatasets() {
        let colors = this.getNColors(3)
        let lowQuartileData = [], interQuartileData = [], topQuartileData = []
        let name = 'social_impact_score'
        let pctLowQuartile = this.socialData[`pct_low_quartile_${name}`]
        let pctTopQuartile = this.socialData[`pct_top_quartile_${name}`]
        let pctInterQuartile = 100 - pctLowQuartile - pctTopQuartile
        lowQuartileData.push(pctLowQuartile);
        interQuartileData.push(pctInterQuartile);
        topQuartileData.push(pctTopQuartile);
        return [
            {data: lowQuartileData, backgroundColor: colors[0],
            label:'Lower quartile'},
            {data: interQuartileData, backgroundColor: colors[1], label: 'Interquartile'},
            {data: topQuartileData, label:'Top quartile', backgroundColor: colors[2]}
        ]
    },
    /* subscore quartile chart */
    subScoreQuartileDatasets() {
        //want to end up with array of three elements, each of which has data corresponding to a quartile
        let lowQuartileData = [], interQuartileData = [], topQuartileData = []
        let colors = this.getNColors(3)
        this.subScoreNames.forEach(name => {
            let pctLowQuartile = this.socialData[`pct_low_quartile_${name}`]
            let pctTopQuartile = this.socialData[`pct_top_quartile_${name}`]
            let pctInterQuartile = 100 - pctLowQuartile - pctTopQuartile
            lowQuartileData.push(pctLowQuartile);
            interQuartileData.push(pctInterQuartile);
            topQuartileData.push(pctTopQuartile);
        })
        return [
            {data: lowQuartileData, backgroundColor: colors[0],
            label: 'Lower quartile'},
            {data: interQuartileData, backgroundColor: colors[1], label: 'Interquartile'},
            {data: topQuartileData, label:'Top quartile', backgroundColor: colors[2]}
        ]
    },

    /* renters chart */
    rentersNamesLabels() {
        let rentersNames = [
            "pct_stressed_renters_income_lte35k",
            "pct_stressed_renters_income_35to75k",
            "pct_stressed_renters_income_gte75k",
        ]
        let rentersLabels = [
            "\u2264 $35K",
            "$35K - $75K",
            "\u2265 $75K"
        ]
        return this.namesLabelsFromArrays(rentersNames, rentersLabels)
    },

    /*economic chart */
    economicNamesLabelsValues() {
        let economicNames = [
            "gini_index",
            "per_capita_income",
            "median_household_income",
            "median_monthly_housing_costs",
            "median_household_discretionary_income",
            "pct_high_income_young_home_owners",
            "pct_under_poverty_line",
            "pct_w_health_insurance_coverage"
        ];
        if(this.isCMBS) {
            economicNames = economicNames.filter(name => name !== 'pct_under_poverty_line')
        }
        let economicLabels = economicNames.map(name => this.stringToName(name).replace('Pct', 'Percent').replace(' W ', ' With '))

        let economicValues = economicNames.map(
            name => {
                let isDollarName = (name) => {
                    return (name.includes('cost') || name.includes('income'))&&!name.includes('young')
                }
                let isPercentName = (name) => {
                    return name.includes('pct')
                }
                if(isDollarName(name)) {
                    return '$'.concat(parseFloat(this.socialData[name]).toLocaleString('en-US'))//toLocaleString adds commas to dollar values
                }
                else if(isPercentName(name)) {
                    return parseFloat(this.socialData[name]).toFixed(1).concat('%')
                }
                else {
                    return parseFloat(this.socialData[name]).toFixed(1)
                }
            })
        let economicNamesLabels = this.namesLabelsFromArrays(economicNames, economicLabels)
        return economicNamesLabels.map((nameLabel, index) => {
            return {...nameLabel, value: economicValues[index]}
        })
    },

    /*work trans percent chart */
    workTransPercentNames() {
        return  ['pct_worktrans_cartruckvan', 'pct_worktrans_publictrans', 'pct_worktrans_walkbike','pct_worktrans_athome'] //cmbs securities have extra breakdown by particular modes of public transport but we're not showing that

    },
    workTransPercentNamesLabels() {
        let workTransPercentLabels = ['Car, Truck, or Van', 'Public Transport', 'Walk or Bike', 'Work at Home'];
        return this.namesLabelsFromArrays(this.workTransPercentNames, workTransPercentLabels)
    },

    /* education chart */
    educationPercentNames() {
        return Object.keys(this.socialData).filter(key => key.includes('ed_pct_25up'))
    },
    educationPercentNamesLabels() {
        let educationPercentLabels = ['Less than high school', 'High school, GED, or equivalent', 'Some college', 'Associate degree', 'Bachelor degree', 'Master degree', 'Professional degree', 'Doctorate degree'];

        return this.namesLabelsFromArrays(this.educationPercentNames, educationPercentLabels)
    },

    /* employment chart */
    employmentPercentNames() {
        return Object.keys(this.socialData).filter(key => {
           return key.includes('pct_rac_')
        })
    },
    employmentPercentNamesLabelsColors() {
        let employmentPercentLabels = [
            'Agriculture, Forestry, Fishing and Hunting',
            'Mining, Quarrying, and Oil and Gas Extraction',
            'Utilities',
            'Construction',
            'Manufacturing',
            'Wholesale Trade',
            'Retail Trade',
            'Transportation and Warehousing',
            'Information',
            'Finance and Insurance',
            'Real Estate and Rental and Leasing',
            'Professional, Scientific, and Technical Services',
            'Management of Companies and Enterprises',
            'Administrative and Support and Waste Management and Remediation Services',
            'Educational Services',
            'Health Care and Social Assistance',
            'Arts, Entertainment, and Recreation',
            'Accommodation and Food Services',
            'Other Services [except Public Administration]',
            'Public Administration'
        ]

        let unsortedNamesLabels = this.namesLabelsFromArrays(this.employmentPercentNames, employmentPercentLabels)

        //sort by percent employed in given sector, descending
        let namesLabels = unsortedNamesLabels.sort((nameLabel1, nameLabel2) => {
            let name1 = nameLabel1.name, name2 = nameLabel2.name;
            let percent1 = this.socialData[name1], percent2 = this.socialData[name2]
            return percent2 - percent1
        })
        let namesLabelsColors = namesLabels.map((nameLabel, index)  => {
            return {...nameLabel, color: this.employmentColors[index] }
        })
        return namesLabelsColors
    }
  },
  methods: {
    /* general */
    concatenatePercent: function(TooltipItem) {
        let label = TooltipItem.dataset.label
        let value = TooltipItem.formattedValue
        return label.concat(': ').concat(value).concat('%')
    },
    openDialog() {
        this.showDialog = true;
    },
    namesLabelsToNames(namesLabels) {
        return namesLabels.map(nameLabel => nameLabel.name)
    },
    showUnderlyingData() {
        return this.showChart(this.workTransPercentNames) || this.showChart(this.educationPercentNames) || this.showChart(this.employmentPercentNames) || this.showChart(this.namesLabelsToNames(this.economicNamesLabelsValues)) || this.showChart(this.namesLabelsToNames(this.commuteTimeNamesLabels))
    },
    showChart(nameArray) {
       if(this.socialData && nameArray.find(name => (this.socialData[name] !== null //note we have to explicitly test for !== null because 0 is falsy
       ))) {
            return true
        }
        else {
            return false
        }
    },
    getNColors(n) {
        /*
        removing the lightest colors from Viridis to solve the problem where boundary between light and dark colors is disproportionally salient
        */
       return chroma.scale(chroma.brewer.Viridis).colors(n + 3).reverse().slice(3)
    },
    namesLabelsFromArrays(nameArray, labelArray) {
        return nameArray.map((name, index) => {
            return {name, label: labelArray[index]}
        })
    },
    stringToName(string) {
        string = string[0].toUpperCase().concat(string.slice(1));  //capitalize first letter

        let index = string.indexOf('_')
        while(index !== -1) {
            //replace '_' with ' ' and capitalize next letter
            let toCapitalize = string[index+1];
            string = string.slice(0, index).concat(' ').concat(toCapitalize.toUpperCase()).concat(string.slice(index+2));
            index = string.indexOf('_')
        }
        return string
    },

    /*social impact chart methods */
    socialImpactChartTooltipTitle: function(tooltip) {
        /* out of the box, chart.js just takes the chart label and makes that be the tooltip title. But the chart label is the array ['Social', 'Impact', 'Score'], so we get the label 'Social,Impact,Score,' and we need to replace the commas with spaces */
        let initialTitle = tooltip[0].label
        let title = initialTitle.replaceAll(',', ' ')
        return title
    },
    socialImpactChartOptions(paddingLeft = 0, fontSize = 8) {
        return {
        layout: {
            padding: {left: paddingLeft}
            },
            maintainAspectRatio: false,
            indexAxis:'y',
            scales: {
                xAxis:
                {
                    max: 100
                },
                yAxis:
                {
                    display: true,
                    ticks:
                    {
                        font:
                        {
                            size: fontSize
                        }
                    }
                }
            },
            plugins:
            {
                legend:
                {
                    display: false
                },
                title:
                {
                    display: true,
                    text:`Social Impact Score: ${this.socialData['social_impact_score']}`,
                    font: {
                        size: 16
                    }
                },
                datalabels:
                {
                    color: 'white',
                    font: {
                        weight: 'bold',
                        size: 14
                    }
                },
                tooltip: {
                    callbacks: {
                        title: this.socialImpactChartTooltipTitle
                    }
                }
            }
        }
    },

    /* subscore chart methods */
    subScoreChartData(shortLabels = false) {
        return {
            labels: shortLabels? this.subScoreLabelsShort : this.subScoreLabels,
            datasets:
            [
                {
                    data: this.subScoreChartRawData,
                    backgroundColor: this.getNColors(10)[6]
                }
            ]
        }
    },
    subScoreChartOptions(paddingLeft = 0, indexAxis = 'y', fontSize = 8, title='Social Impact Component Scores', isPercent = false) {
       return  {
            layout: {
                padding: {
                    left: paddingLeft
                }
            },
            maintainAspectRatio: false,
            indexAxis: indexAxis,
            scales:
            {
                xAxis:
                    {
                        max: 100,
                        ticks: {
                            autoSkip: false,
                            font: {
                                size: fontSize
                            }
                        },
                        grid: {
                            display: indexAxis !== 'y'? false : true
                    },
                    },
                yAxis:
                {   grid: {
                        display: indexAxis === 'y'? false : true
                    },
                    ticks:
                    {
                        font:
                        {
                            size: fontSize
                        },
                    },
                    max: 100
                }
            },
            plugins:
            {
                legend:
                {
                    display: false
                },
                title:
                {
                    display: true,
                    text: title,
                    font: {
                        size: 16
                    }
                },
            }
        }
    },

    /*bar chart methods */
    datasetsFromNamesAndLabels(nameLabelArray, reverse=false, useEmploymentColors = false) {
        let length = nameLabelArray.length
        let colors = reverse? this.getNColors(length).reverse() : this.getNColors(length)
        if(useEmploymentColors) {
            colors = this.employmentColors
        }
        let datasets = [];
        let sum = 0
        nameLabelArray.forEach((nameLabel, index) => {
            let name = nameLabel.name
            datasets.push({
                data: [this.socialData[name]],
                backgroundColor: colors[index],
                label: nameLabel.label
            })
            sum = sum + this.socialData[name]
        })
        if(sum < 100 - 0.01) {
            datasets.push({
                data: [100 - sum],
                backgroundColor: '#dedcdc',
                label: 'No Data'
            })
        }
        return datasets;
    },
    barChartOptions(title, showLegend = true, padding = 0) {
        return {
            layout: {
                padding: {
                    right: padding,
                    left: padding,
                    //autoPadding: false
                }
            },
            maintainAspectRatio: false,
            indexAxis:'y',
            scales:
            {
                y:
                {
                    stacked: true,
                    display: false,
                    //offset: false
                },
                x:
                {
                    stacked: true,
                    padding: 0,
                    position: 'top',
                    max: 100
                    //display: false,
                },
            },
            datasets:
            {
                bar: {
                    //maxBarThickness: 30,
                    barPercentage: 1,
                    categoryPercentage: 1,
                }
            },
            plugins:
            {
                title:
                {
                    text: title,
                    display: true
                },
                legend:
                {   onClick: null,
                    display: showLegend,
                    align: 'start'
                },
                tooltip: {
                    callbacks: {
                        label: this.concatenatePercent
                    }
                }

            }
        }
    },
    /*quartile chart methods */
    quartileChartOptions(title, displayLegend) {
        //we're setting 'color: "white"' on a bunch of stuff as a hacky way to not display grid lines and labels. This is because setting "display: false" changes the way chart.js computes the height of the bars, and we want these bars to align with the social score summary bars, which do have grid lines
        return {
            maintainAspectRatio: false,
            layout: {
                padding: {
                    left: 50,
                    //right: 200
                }
            },
            indexAxis:'y',
            scales:
            {
                y:
                {
                    stacked: true,
                    color: 'white',
                    grid: {
                        color: 'white'
                    },
                    display: false
                },
                x:
                {   grid: {
                        //color: 'white'
                    },
                    stacked: true,
                    //color: 'white',
                    ticks: {
                        //color: 'white'
                    },
                },
            },
            plugins:
            {
                title:
                {
                    text: title,
                    display: true
                },
                legend:
                {
                    display: displayLegend,
                    align: 'start',
                    onClick: null, //removes cross-out behavior
                    labels: {
                        font: {
                            size: this.quartileChartFontSize
                        }
                    }
                },
                tooltip: {
                    callbacks: {
                        label: this.concatenatePercent
                    }
                }
            }
        }
    },
    /* powerpoint methods */
    updatePowerpoint: function() {
        this.updatePowerpointUrl('social-impact-chart', 'socialImpactUrl');
        this.updatePowerpointUrl('subscore-chart', 'subscoreUrl')
        this.updatePowerpointDimensions('social-impact-chart', 'socialImpactDimensions')
        this.updatePowerpointDimensions('subscore-chart', 'subscoreDimensions')
    },
    updatePowerpointDimensions: function(id, key) {
        let chartCanvas = document.getElementById(id)
        let width = chartCanvas.width
        let height = chartCanvas.height
        let powerpointData = this.powerpointData
        powerpointData[key] = { width, height }
        this.$store.dispatch('updateState', ['powerpointData', powerpointData])
    },
    updatePowerpointUrl: function(id, key) {
        let chartCanvas = document.getElementById(id)

        var dataUrl = chartCanvas.toDataURL();
        let powerpointData = this.powerpointData
        powerpointData[key] = dataUrl
        this.$store.dispatch('updateState', ['powerpointData', powerpointData])
    },

  },
  watch: {
    startPowerpointGeneration(newVal) {
        if(newVal === true) {
            //setting timeout to give the chart time to display before we capture its url
            window.setTimeout(() => {
                this.updatePowerpoint()
                let powerpointDataToGenerate = this.powerpointDataToGenerate.filter(name => (name !== 'socialImpactUrl') && (name !== 'subscoreUrl') && (name !== 'subscoreDimensions') && (name !== 'socialImpactDimensions'))
                this.$store.dispatch('updateState', ['powerpointDataToGenerate', powerpointDataToGenerate])
            },
            500)
        }
    }
  }

}
</script>
<style scoped>
.rscore, span.rscore {
    background: linear-gradient(321deg,rgba(0, 158, 216,1.0) 0%,rgba(83,201,0,1.0) 100%);
        -webkit-text-fill-color: transparent;
        -webkit-background-clip: text;
    font-weight: bold;
}
table td {
  padding-right: 5px;
  padding-left: 5px;
}
</style>
<style>
.mainRow td {
  font-size:1.2em;
  padding:5px;
  padding-right: 2px !important;
  font-weight:0600;
}
</style>
